import * as React from 'react';
import { ComponentProps } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { cn } from '../../utils';

export type DialogProps = DialogPrimitive.DialogProps &
  ComponentProps<typeof DialogPrimitive.Content> & {
    className?: string;
    title?: string;
    description?: string;
  };

export const Dialog = ({
  className,
  children,
  title,
  description,
  open,
  onOpenChange,
  ...props
}: DialogProps) => (
  <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
    <DialogPrimitive.Portal>
      <div className="sdk">
        {/*TODO @Mike make this app wide compatible with smaller ranges. Savanna has a default z-index 1000 on the header*/}
        <DialogPrimitive.Overlay className="fixed z-[1001] inset-0 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 flex justify-center items-center">
          <DialogPrimitive.Content
            onOpenAutoFocus={(e) => e.preventDefault()}
            className={cn(
              'flex flex-col max-h-full px-6 overflow-hidden fixed left-[50%] top-[50%] z-[1001] w-full md:w-[668px] translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg',
              className
            )}
            {...props}
          >
            <div className="flex flex-col gap-1.5 text-center sm:text-left pt-6">
              {title && (
                <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                  {title}
                </DialogPrimitive.Title>
              )}
              {description && (
                <DialogPrimitive.Description className="text-sm text-muted-foreground">
                  {description}
                </DialogPrimitive.Description>
              )}
            </div>
            <div className="flex-1 overflow-auto grid pb-6 -mr-6 pr-6">
              {children}
            </div>
            {onOpenChange && (
              <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </div>
    </DialogPrimitive.Portal>
  </DialogPrimitive.Root>
);
Dialog.displayName = DialogPrimitive.Dialog.displayName;

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-4 gap-3',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

export const ModalFooter = DialogFooter;
